import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { decode } from 'blurhash';
import styles from './styles.module.css';
import clsx from 'clsx';

interface Props {
  alt: string;
  width?: number | string;
  height?: number | string;
  blurhash?: string;
  src?: string;
  className?: string;
  objectFit?: 'cover' | 'contain';
  sizes?: string;
}

const DEFAULT_SIZES = '(max-width: 769px) 160px, 290px';

export const BlurHashImage = ({
  width,
  height,
  alt = '',
  blurhash,
  src = '',
  className = '',
  objectFit = 'cover',
  sizes = DEFAULT_SIZES,
}: Props) => {
  const [placeholder, setPlaceholder] = useState('');
  const [loaded, setLoaded] = useState(false);

  const fillsParent = width === undefined && height === undefined;

  useEffect(() => {
    if (!blurhash) {
      return;
    }

    try {
      const pixels = decode(blurhash, 32, 32);
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');
      const imageData = ctx?.createImageData(32, 32);
      if (imageData) {
        imageData.data.set(pixels);
        ctx?.putImageData(imageData, 0, 0);
        setPlaceholder(canvas.toDataURL());
      }
    } catch {
      setPlaceholder('');
    }
  }, [blurhash]);

  const imageStyle = {
    objectFit,
    objectPosition: 'center',
  } as const;

  return (
    <div
      className={clsx(
        styles.imageContainer,
        fillsParent && styles.fillContainer,
        className
      )}
      style={fillsParent ? undefined : { width, height }}
    >
      {placeholder && (
        <Image
          src={placeholder}
          alt=""
          aria-hidden
          fill
          sizes={sizes}
          className={styles.placeholder}
          style={imageStyle}
        />
      )}
      {src && (
        <Image
          className={clsx(styles.media, loaded ? styles.fadeIn : styles.image)}
          src={src}
          alt={alt}
          fill
          sizes={sizes}
          style={imageStyle}
          onLoad={() => setLoaded(true)}
        />
      )}
    </div>
  );
};
